import { createReducer, on } from '@ngrx/store';
import { Action, ActionReducer } from '@ngrx/store/src/models';
import { LoadError, LoadFilter } from './models';
import { FilterChangedProps, LoadSucceededProps, LoadActions } from './actions';
import { mergeReducers } from '../utils';

export interface LoadState<T> {
  ids: string[];
  entities: { [key: string]: T };
  primaryKey: string;
  loading: boolean;
  error: LoadError | null;
  filter: LoadFilter;
}

export const resetEntities = <Entity>(state: LoadState<Entity>): LoadState<Entity> => ({
  ...state,
  ids: [],
  entities: {},
});

export const resetFilter = <Entity>(state: LoadState<Entity>): LoadState<Entity> => ({
  ...state,
  filter: null,
});

export const setEntities = <Entity extends object>(
  state: LoadState<Entity>,
  { entities }: LoadSucceededProps<Entity>,
): LoadState<Entity> => {
  const { primaryKey } = state;
  const indexableEntities = entities.filter((entity) => primaryKey in entity);
  const ids = indexableEntities.map((entity) => entity[primaryKey]);
  const indexedEntities = indexableEntities.reduce((accumulator, entity) => {
    const key = entity[primaryKey];

    accumulator[key] = entity;

    return accumulator;
  }, {});

  return {
    ...state,
    ids,
    entities: indexedEntities,
  };
};

export const setError = <Entity>(state: LoadState<Entity>, { error }): LoadState<Entity> => ({
  ...state,
  error,
  ids: [],
  entities: {},
});

export const changeFilter = <Entity>(
  state: LoadState<Entity>,
  { filter }: FilterChangedProps,
): LoadState<Entity> => ({ ...state, filter });

export const setLoading = <Entity>(state: LoadState<Entity>): LoadState<Entity> => ({
  ...state,
  loading: true,
});

export const resetLoading = <Entity>(state: LoadState<Entity>): LoadState<Entity> => ({
  ...state,
  loading: false,
});

export const resetError = <Entity>(state: LoadState<Entity>): LoadState<Entity> => ({
  ...state,
  error: null,
});

export const loadReducer = mergeReducers(resetEntities, resetFilter, setLoading, resetError);
export const filterChangedReducer = mergeReducers(changeFilter, setLoading, resetError);
export const loadSucceededReducer = mergeReducers(setEntities, resetLoading);
export const loadErroredReducer = mergeReducers(setError, resetLoading);

export function createLoadReducer<Entity>(
  initialState: LoadState<Entity>,
  actions: LoadActions<Entity>,
): ActionReducer<LoadState<Entity>, Action> {
  return createReducer(
    initialState,
    on(actions.load, loadReducer),
    on(actions.filterChanged, filterChangedReducer),
    on(actions.loadSucceeded, loadSucceededReducer),
    on(actions.loadErrored, loadErroredReducer),
  );
}
